import React, { useState } from 'react';
import { Database, RefreshCw, CheckCircle, AlertCircle, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { runMigration } from '../../utils/migrationHelper';

const MigrateTests = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({
    success: 0,
    failed: 0,
    skipped: 0,
    errors: [],
  });
  const [logs, setLogs] = useState([]);

  const migrateTests = async () => {
    setIsProcessing(true);
    setResults({ success: 0, failed: 0, skipped: 0, errors: [] });
    setLogs([]);

    try {
      await runMigration(
        // Progress callback
        (progressData) => {
          setProgress({ current: progressData.current, total: progressData.total });
        },
        // Result callback
        (resultData) => {
          setResults(resultData);
        },
        // Log callback
        (message, type) => {
          setLogs(prev => [...prev, `[${type.toUpperCase()}] ${message}`]);

          // Show toast for important messages
          if (type === 'success' && message.includes('✅')) {
            toast.success(message.split('✅')[1]?.trim() || message);
          } else if (type === 'error' && message.includes('❌')) {
            toast.error(message.split('❌')[1]?.trim() || message);
          }
        }
      );

      toast.success('Migration completed successfully!');
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(`Migration failed: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Database className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Migrate Existing Tests</h1>
            <p className="text-gray-600">
              Classify and tag all existing tests in the database
            </p>
          </div>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-1">Important Notes:</h3>
              <ul className="text-yellow-800 text-sm space-y-1 list-disc list-inside">
                <li>This will classify ALL tests that don't have classification data</li>
                <li>Tests already classified will be skipped</li>
                <li>This process uses AI and may take several minutes</li>
                <li>Each test takes ~1-2 seconds to process</li>
                <li>Make sure you have a stable internet connection</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Processing tests...
              </span>
              <span className="text-sm text-gray-600">
                {progress.current} / {progress.total}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Results */}
        {(results.success > 0 || results.failed > 0 || results.skipped > 0) && (
          <div className="mb-6 grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-900">{results.success}</div>
              <div className="text-sm text-green-700">Migrated</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <Zap className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-900">{results.skipped}</div>
              <div className="text-sm text-blue-700">Skipped</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-red-900">{results.failed}</div>
              <div className="text-sm text-red-700">Failed</div>
            </div>
          </div>
        )}

        {/* Live Logs */}
        {logs.length > 0 && (
          <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
              <RefreshCw className="w-4 h-4 mr-2" />
              Live Logs
            </h3>
            <div className="bg-gray-900 text-green-400 text-xs font-mono p-4 rounded max-h-96 overflow-y-auto">
              {logs.map((log, index) => (
                <div key={index} className="mb-1">
                  {log}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Errors */}
        {results.errors.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-red-900 mb-2">Errors:</h3>
            <ul className="text-red-800 text-sm space-y-1 max-h-60 overflow-y-auto">
              {results.errors.map((error, index) => (
                <li key={index} className="font-mono text-xs">
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Button */}
        <button
          onClick={migrateTests}
          disabled={isProcessing}
          className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-colors ${
            isProcessing
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isProcessing ? (
            <>
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Database className="w-5 h-5 mr-2" />
              Start Migration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default MigrateTests;
